import { TFunction } from "i18next";
import { Ampersand, ArrowDownRight, ArrowRight, ArrowUpRight, CalendarDays, CalendarRange, CircleCheck, Crosshair, Eraser, Euro, Fingerprint, Hash, Link, ListChecks, Minus, MousePointerClick, Percent, Sigma, SquareCheckBig, Tally5, TriangleAlert, Type, TypeOutline, UserRound, Globe } from "lucide-react";
import React from "react";
import { useModelStore } from "src/store";
import { PeriodTypeMonth, PeriodTypeQuarter, PeriodTypeWeek } from "./period";
import { Filter, Path } from "./records";

export interface Field {
  _key: string;
  name: string;
  type: FieldType;
  readOnly: boolean;
  formula: string;
  order: number;
  format: Map<string, any> | undefined;
  typeItems: Map<string, any> | undefined;
  aggregation: AggregationType | undefined;
  description?: string;
  defaultValue?: any;
}

/** Type changes currently possible for a field (GET .../fields/{fieldKey}/typechanges) */
export interface FieldTypeChanges {
  /** At least one record stores a value for the field */
  hasValues: boolean;
  /** Target types the field can be changed into right now */
  allowed: FieldType[];
  /** Target types needing a conversion of the stored values (not available yet) */
  convertible: FieldType[];
}

export enum FieldType {
  Text = "STR",
  TextArea = "TEXT_AREA",
  Number = "INT",
  Decimal = "DEC",
  Devise = "DEV",
  Percent = "PERCENT",
  Boolean = "BOOL",
  Date = "DATE",
  Period = "PERIOD",
  Choice = "CHOICE",
  MultiChoice = "MULTI_CHOICE",
  User = "USER",
  Link = "LINK",
  Url = "URL",
  Button = "BUTTON",
}

export function fieldTypeIcon(type: FieldType | undefined) {
  if (!type) {
    return TriangleAlert;
  }
  switch (type as FieldType) {
    case FieldType.Text:
      return Type;
    case FieldType.TextArea:
      return TypeOutline;
    case FieldType.Number:
      return Hash;
    case FieldType.Decimal:
      return Hash;
    case FieldType.Devise:
      return Euro;
    case FieldType.Boolean:
      return SquareCheckBig;
    case FieldType.Date:
      return CalendarDays;
    case FieldType.Period:
      return CalendarRange;
    case FieldType.Percent:
      return Percent;
    // case FieldType.Duration:
    //   return Hourglass;
    // case FieldType.DateTime:
    //   return CalendarClock;
    case FieldType.Choice:
      return CircleCheck;
    case FieldType.MultiChoice:
      return ListChecks;
    case FieldType.User:
      return UserRound;
    case FieldType.Link:
      return Link;
    case FieldType.Url:
      return Globe;
    case FieldType.Button:
      return MousePointerClick;
    default:
      return TriangleAlert;
  }
}

export function shouldDisplayInTooltip(type: FieldType | undefined | null) {
  return type && type !== FieldType.Link && type !== FieldType.Boolean;
}

export const TYPE_ITEMS_BUTTON_URL = "buttonUrl";
export const TYPE_ITEMS_BUTTON_METHOD = "buttonMethod";
export const TYPE_ITEMS_BUTTON_MAPPING = "buttonMapping";
export const TYPE_ITEMS_BUTTON_LABEL = "buttonLabel";
export const TYPE_ITEMS_BUTTON_COLOR = "buttonColor";
export const TYPE_ITEMS_BUTTON_SUCCESS_MSG = "buttonSuccessMsg";
export const TYPE_ITEMS_BUTTON_ERROR_MSG = "buttonErrorMsg";
export const TYPE_ITEMS_BUTTON_REFRESH = "buttonRefresh";
export const TYPE_ITEMS_BUTTON_REFRESH_ROW = "buttonRefreshRow";
export const TYPE_ITEMS_BUTTON_CONFIRM_MSG = "buttonConfirmMsg";

export const TYPE_ITEMS_OBJECT_KEY = "objectKey";
export const TYPE_ITEMS_RELATION_KEY = "relationKey";
export const TYPE_ITEMS_FIELD_KEY = "fieldKey";
export const TYPE_ITEMS_RELATION_NAME = "relationName";
export const TYPE_ITEMS_RELATION_CARDINALITY = "relationCardinality";
export const TYPE_ITEMS_RELATION_FILTER = "relationFilter";
export const TYPE_ITEMS_CHOICE_OPTIONS = "choiceOptions";
export const TYPE_ITEMS_PERIOD_TYPE = "periodType";
export const TYPE_ITEMS_DATE_FORMAT = "dateFormat";
export const FIELD_TYPES_WITH_FORMULA = [FieldType.Devise, FieldType.Decimal, FieldType.Percent, FieldType.Number, FieldType.Date, FieldType.Period, FieldType.Text, FieldType.Choice, FieldType.MultiChoice, FieldType.Boolean];
export const FIELD_TYPES_WITH_FORMAT = [FieldType.Devise, FieldType.Decimal, FieldType.Percent, FieldType.Number, FieldType.Date, FieldType.Period, FieldType.Text, FieldType.TextArea, FieldType.Url];
export const FIELD_TYPES_WITH_DEFAULT_VALUE = [FieldType.Devise, FieldType.Decimal, FieldType.Percent, FieldType.Number, FieldType.Text, FieldType.Choice, FieldType.MultiChoice, FieldType.Boolean];

export interface FieldLink {
  objectKey: string;
  fieldKey: string;
  linkName: string;
}

export interface FieldChoice {
  name: string;
  color: string;
}

export function fieldChoiceByNames(options: FieldChoice[]): Map<string, FieldChoice> {
  if (!options) {
    return new Map();
  }
  return new Map(options.map(option => [option.name, option]));
}


export function fieldTypeToIcon(type: FieldType, className: string): React.FunctionComponentElement<React.SVGProps<SVGSVGElement>> {
  return React.createElement(fieldTypeIcon(type), { className: className });
}


export enum AggregationType {
  NO_AGGR = "NO_AGGR",
  SUM = "SUM",
  AVG = "AVG",
  COUNT = "COUNT",
  COUNT_DISTINCT = "COUNT_DISTINCT",
  MIN = "MIN",
  MAX = "MAX",
  CONCAT = "CONCAT",
  YEAR = "YEAR",
  QUARTER = "QUARTER",
  MONTH = "MONTH",
  WEEK = "WEEK",
  DAY = "DAY",
  VALUE = "VALUE",
}

// isCountAggregation tells whether the aggregation counts values (COUNT or
// COUNT_DISTINCT): the cells then display integers whatever the field type.
export function isCountAggregation(aggregation: AggregationType | null | undefined): boolean {
  return aggregation === AggregationType.COUNT || aggregation === AggregationType.COUNT_DISTINCT;
}

export function aggrFilterLabel(t: TFunction, aggregation: AggregationType | undefined): string {
  if (!aggregation) {
    return "";
  }
  if (aggregation === AggregationType.NO_AGGR || aggregation === AggregationType.VALUE || aggregation === AggregationType.CONCAT) {
    return "";
  }
  return t(`field.aggregation.label.${aggregation}`);
}

export function validAggregationByType(type: FieldType | undefined, typeItems: Map<string, any> | undefined): AggregationType[] {

  if (!type) {
    return [AggregationType.NO_AGGR];
  }

  const fieldByKeys = useModelStore.getState().fieldByKeys;

  if (type === FieldType.Link) {
    const fieldKey = typeItems?.get(TYPE_ITEMS_FIELD_KEY);

    let aggregationTypes: AggregationType[] = [];
    if (fieldKey) {
      const field = fieldByKeys?.get(fieldKey!);
      if (field) {
        // A link whose destination field is itself a link (a relation chain)
        // recurses: CONCAT is added once, at the outermost level.
        const destAggregationTypes = validAggregationByType(field.type, field.typeItems);
        if (destAggregationTypes.includes(AggregationType.CONCAT)) {
          return destAggregationTypes;
        }
        return [...destAggregationTypes, AggregationType.CONCAT];
      }
    }
    return aggregationTypes;
  }

  switch (type) {
    case FieldType.Text:
      return [AggregationType.NO_AGGR, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    case FieldType.TextArea:
      return [AggregationType.NO_AGGR, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    case FieldType.Url:
      return [AggregationType.NO_AGGR, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    case FieldType.Boolean:
      return [AggregationType.NO_AGGR, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    case FieldType.Date:
      return [AggregationType.NO_AGGR, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE, AggregationType.YEAR, AggregationType.QUARTER, AggregationType.MONTH, AggregationType.WEEK];
    case FieldType.Period:
      return [AggregationType.NO_AGGR, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE, AggregationType.YEAR, AggregationType.QUARTER, AggregationType.MONTH, AggregationType.WEEK];
    case FieldType.Choice:
      return [AggregationType.NO_AGGR, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    case FieldType.MultiChoice:
      return [AggregationType.NO_AGGR, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    case FieldType.User:
      return [AggregationType.NO_AGGR, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    // case FieldType.DateTime:
    //   return [AggregationType.NO_AGGR, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.CONCAT];
    // case FieldType.Duration:
    //   return [AggregationType.NO_AGGR, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.CONCAT];
    case FieldType.Decimal:
      return [AggregationType.NO_AGGR, AggregationType.SUM, AggregationType.AVG, AggregationType.MIN, AggregationType.MAX, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    case FieldType.Devise:
      return [AggregationType.NO_AGGR, AggregationType.SUM, AggregationType.AVG, AggregationType.MIN, AggregationType.MAX, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    case FieldType.Number:
      return [AggregationType.NO_AGGR, AggregationType.SUM, AggregationType.AVG, AggregationType.MIN, AggregationType.MAX, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    case FieldType.Percent:
      return [AggregationType.NO_AGGR, AggregationType.SUM, AggregationType.AVG, AggregationType.MIN, AggregationType.MAX, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    default:
      return [];
  }
}

export function validNumericAggregationByType(type: FieldType | undefined, typeItems: Map<string, any> | undefined): AggregationType[] {

  if (!type) {
    return [];
  }

  const fieldByKeys = useModelStore.getState().fieldByKeys;

  if (type === FieldType.Link) {
    const fieldKey = typeItems?.get(TYPE_ITEMS_FIELD_KEY);

    let aggregationTypes: AggregationType[] = [];
    if (fieldKey) {
      const field = fieldByKeys?.get(fieldKey!);
      if (field) {
        return validNumericAggregationByType(field.type, field.typeItems);
      }
    }
    return aggregationTypes;
  }

  switch (type) {
    case FieldType.Text:
      return [AggregationType.COUNT, AggregationType.COUNT_DISTINCT];
    case FieldType.TextArea:
      return [AggregationType.COUNT, AggregationType.COUNT_DISTINCT];
    case FieldType.Url:
      return [AggregationType.COUNT, AggregationType.COUNT_DISTINCT];
    case FieldType.Boolean:
      return [AggregationType.COUNT, AggregationType.COUNT_DISTINCT];
    case FieldType.Date:
      return [AggregationType.COUNT, AggregationType.COUNT_DISTINCT];
    case FieldType.Period:
      return [AggregationType.COUNT, AggregationType.COUNT_DISTINCT];
    case FieldType.Choice:
      return [AggregationType.COUNT, AggregationType.COUNT_DISTINCT];
    case FieldType.MultiChoice:
      return [AggregationType.COUNT, AggregationType.COUNT_DISTINCT];
    case FieldType.User:
      return [AggregationType.COUNT, AggregationType.COUNT_DISTINCT];
    case FieldType.Decimal:
      return [AggregationType.SUM, AggregationType.AVG, AggregationType.MIN, AggregationType.MAX, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    case FieldType.Devise:
      return [AggregationType.SUM, AggregationType.AVG, AggregationType.MIN, AggregationType.MAX, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    case FieldType.Number:
      return [AggregationType.SUM, AggregationType.AVG, AggregationType.MIN, AggregationType.MAX, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    case FieldType.Percent:
      return [AggregationType.SUM, AggregationType.AVG, AggregationType.MIN, AggregationType.MAX, AggregationType.COUNT, AggregationType.COUNT_DISTINCT, AggregationType.VALUE];
    default:
      return [];
  }
}

export function getDestinationField(field: Field, fieldByKeys: Map<string, Field> | null): Field | undefined {
  if (field.type === FieldType.Link) {
    const typeItems = field.typeItems as Map<string, any>;
    const fieldKey = typeItems?.get(TYPE_ITEMS_FIELD_KEY);
    if (fieldKey) {
      const linkedField = fieldByKeys?.get(fieldKey);
      if (linkedField) {
        return getDestinationField(linkedField, fieldByKeys);
      }
    }
    return undefined;
  }
  return field;
}

// Aggregation applied to a column: the view override (per path key) wins over
// the default carried by the field.
export function columnAggregation(field: Field | null | undefined, viewAggregations: Map<string, AggregationType> | null | undefined, pathKey: string | null | undefined): AggregationType {
  const viewAggregation = pathKey ? viewAggregations?.get(pathKey) : undefined;
  return viewAggregation ?? field?.aggregation ?? AggregationType.NO_AGGR;
}

export function fieldToOutputType(field: Field, aggregation: AggregationType | undefined, fieldByKeys: Map<string, Field> | null, linkRecursion: boolean = false): FieldType {
  if (linkRecursion && field.type === FieldType.Link) {
    const typeItems = field.typeItems as Map<string, any>;
    const fieldKey = typeItems?.get(TYPE_ITEMS_FIELD_KEY);
    if (fieldKey) {
      const linkedField = fieldByKeys?.get(fieldKey);
      if (linkedField) {
        return fieldToOutputType(linkedField, aggregation, fieldByKeys, true);
      }
    }
    return field.type;
  }
  if (!aggregation || aggregation === AggregationType.NO_AGGR || (!linkRecursion && field.type === FieldType.Link)) {
    return field.type;
  }
  return fieldTypeToOutputType(field.type, aggregation);
}

export function fieldTypeToOutputType(fieldType: FieldType, aggregation: AggregationType | undefined): FieldType {

  if (!aggregation || aggregation === AggregationType.NO_AGGR || fieldType === FieldType.Link) {
    return fieldType;
  }
  return aggregationTypeToOutputType(fieldType, aggregation);
}

function aggregationTypeToOutputType(fieldType: FieldType, aggregation: AggregationType | undefined): FieldType {
  switch (aggregation) {
    case AggregationType.COUNT:
      return FieldType.Number;
    case AggregationType.COUNT_DISTINCT:
      return FieldType.Number;
    case AggregationType.CONCAT:
      return FieldType.Text;
    case AggregationType.SUM:
      return fieldType;
    case AggregationType.AVG:
      return fieldType;
    case AggregationType.MIN:
      return fieldType;
    case AggregationType.MAX:
      return fieldType;
    case AggregationType.YEAR:
      return FieldType.Period;
    case AggregationType.QUARTER:
      return FieldType.Period;
    case AggregationType.MONTH:
      return FieldType.Period;
    case AggregationType.WEEK:
      return FieldType.Period;
    case AggregationType.DAY:
      return FieldType.Period;
    default:
      return fieldType;
  }
}

export function aggregationTypeIcon(aggregation: AggregationType | null) {
  if (!aggregation) {
    return Eraser;
  }
  switch (aggregation) {
    case AggregationType.COUNT:
      return Tally5;
    case AggregationType.COUNT_DISTINCT:
      return Fingerprint;
    case AggregationType.CONCAT:
      return Ampersand;
    case AggregationType.SUM:
      return Sigma;
    case AggregationType.AVG:
      return ArrowRight;
    case AggregationType.MIN:
      return ArrowDownRight;
    case AggregationType.MAX:
      return ArrowUpRight;
    case AggregationType.YEAR:
      return CalendarDays;
    case AggregationType.QUARTER:
      return CalendarDays;
    case AggregationType.MONTH:
      return CalendarDays;
    case AggregationType.WEEK:
      return CalendarDays;
    case AggregationType.DAY:
      return CalendarDays;
    case AggregationType.VALUE:
      return Crosshair;
    case AggregationType.NO_AGGR:
      return Minus;
    default:
      return TriangleAlert;
  }
}

export function aggregationTypeToIcon(aggregation: AggregationType | null, className: string): React.FunctionComponentElement<React.SVGProps<SVGSVGElement>> {
  return React.createElement(aggregationTypeIcon(aggregation), { className: className });
}

/**
 * i18n key of the one-line help displayed under an option of the column
 * aggregation menu: what the option does to the rows of the view.
 * `null` is the "default aggregation" option (removes the view override).
 * On a link column, "no aggregation" keys the rows on the LINKED record (the
 * record holding the displayed value), not on the displayed value.
 */
export function aggregationHelpKey(aggregation: AggregationType | null, isLinkColumn: boolean): string {
  switch (aggregation) {
    case null:
      return "field.aggregation.help.default";
    case AggregationType.NO_AGGR:
      return isLinkColumn ? "field.aggregation.help.key.link" : "field.aggregation.help.key";
    case AggregationType.VALUE:
      return "field.aggregation.help.value";
    case AggregationType.COUNT:
      return "field.aggregation.help.count";
    case AggregationType.COUNT_DISTINCT:
      return "field.aggregation.help.count_distinct";
    case AggregationType.CONCAT:
      return "field.aggregation.help.concat";
    case AggregationType.SUM:
      return "field.aggregation.help.sum";
    case AggregationType.AVG:
      return "field.aggregation.help.avg";
    case AggregationType.MIN:
      return "field.aggregation.help.min";
    case AggregationType.MAX:
      return "field.aggregation.help.max";
    case AggregationType.YEAR:
      return "field.aggregation.help.year";
    case AggregationType.QUARTER:
      return "field.aggregation.help.quarter";
    case AggregationType.MONTH:
      return "field.aggregation.help.month";
    case AggregationType.WEEK:
      return "field.aggregation.help.week";
    case AggregationType.DAY:
      return "field.aggregation.help.day";
    default:
      return "field.aggregation.help.computed";
  }
}

export function hasFormatting(field: Field): boolean {
  if (!field) {
    return false;
  }
  if (field.type === FieldType.Period) {
    const periodType = field.typeItems?.get(TYPE_ITEMS_PERIOD_TYPE);
    if (periodType && periodType === PeriodTypeMonth || periodType === PeriodTypeWeek || periodType === PeriodTypeQuarter) {
      return true;
    }
    return false;
  } else {
    return FIELD_TYPES_WITH_FORMAT.includes(field.type);
  }
}

export function hasDefaultValueBeforeTypeItems(type: FieldType) {
  return FIELD_TYPES_WITH_DEFAULT_VALUE.includes(type) && type != FieldType.Choice && type != FieldType.MultiChoice
}

export function hasDefaultValueAfterTypeItems(type: FieldType) {
  return FIELD_TYPES_WITH_DEFAULT_VALUE.includes(type) && (type == FieldType.Choice || type == FieldType.MultiChoice)
}

export default Field;

export interface AggregationFormula {
  path: Path;
  filter?: Filter | null;
  aggregation: AggregationType;
}